import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {OfferInfo} from "~/app/core/model/offer-info";
import {ApiService} from "~/app/core/api.service";
import {OfferItemView} from "~/app/components/offer-item/offer-item-view";
import {OfferItemAction, OfferItemActionType} from "~/app/components/offer-item/offer-item-actions";
import * as Log from "~/app/core/log";
import {Settings} from "~/app/core/model/settings";
import {NotificationService} from "~/app/core/notification.service";
import {momentNow} from "~/app/core/model/date-str";
import {isWithinWeeklyHours} from "~/app/core/model/weekly-hours";

@Component({
    selector: "OfferItem",
    templateUrl: "./offer-item.component.html",
    styleUrls: ["./offer-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferItemComponent implements OnInit, AfterViewInit, OnDestroy {
    @Output() readonly action = new EventEmitter<OfferItemAction>();

    @Input() set offerInfo(offerInfo: OfferInfo) {
        if (this._offerInfo !== offerInfo) {
            this._offerInfo = offerInfo;
            this._daysRemaining = null;
            this.cd.detectChanges();
        }
    }
    get offerInfo(): OfferInfo {
        return this._offerInfo;
    }
    get openOfferInfo(): OfferInfo {
        return this._openOfferInfo;
    }
    @Input() set offerItemView(offerItemView: OfferItemView) {
        if (this._offerItemView !== offerItemView) {
            this._offerItemView = offerItemView;
            this.cd.detectChanges();
        }
    }

    @Input() set displayTime(displayTime: moment.Moment) {
        if (this._displayTime !== displayTime) {
            this._displayTime = displayTime;
            this.cd.detectChanges();
        }
    }

    get displayTime(): moment.Moment {
        return this._displayTime;
    }

    get viewShowRedeemNow(): boolean {
        return this._offerItemView ? this._offerItemView.showRedeemNow : false;
    }

    get viewMilesAway(): number {
        return this._offerItemView ? this._offerItemView.milesAway : null;
    }

    get viewCityName(): string {
        return this._offerItemView ? this._offerItemView.city : null;
    }

    get viewUnavailable(): boolean {
        return this._offerItemView ? this._offerItemView.unavailable : false;
    }

    get viewShowExpiration(): boolean {
        return this._offerItemView ? this._offerItemView.showExpiration : false;
    }

    get viewFraction(): number {
        return this._offerItemView ? this._offerItemView.fraction : 0.0;
    }

    get settings(): Settings {
        return this.api.device_loadSettings();
    }

    get obscured(): boolean {
        return this.settings.obscureUnauthenticatedOffer && !this.api.user_isAuthenticated();
    }

    get mystery(): boolean {
        return this._offerItemView ? this._offerItemView.showMystery : false;
    }

    get revealCost(): number {
        return this._offerInfo ? this._offerInfo.revealCost : 0;
    }

    get canReveal(): boolean {
        return this._offerInfo && (this._offerInfo.revealed || (this._offerInfo.revealCost <= this.api.tickets_getTicketCount()));
    }

    get repeatUnlockCost(): number {
        return this._offerInfo ? this._offerInfo.repeatUnlockCost : 0;
    }

    get canRepeatUnlock(): boolean {
        return this._offerInfo && (this._offerInfo.repeatUnlocked || (this._offerInfo.repeatUnlockCost <= this.api.tickets_getTicketCount()));
    }

    get negativeOfferId(): boolean {
        return this._offerInfo && this._offerInfo.id < 0;
    }

    get isOfferOnlineOnly(): boolean {
        return this._offerInfo && !!this._offerInfo.onlineOnly;
    }

    get isOfferRepeatUnlockable(): boolean {
        return this.settings.gamificationEnabled && OfferItemView.isOfferRepeatUnlockable(this.offerInfo, this.settings.calculateCurrentTime());
    }

    get isOfferRedeemed(): boolean {
        return OfferItemView.isOfferRedeemed(this.offerInfo, this.settings.calculateCurrentTime());
    }

    get isOfferFavorited(): boolean {
        return !this.settings.gamificationFavoriteMode || OfferItemView.isOfferFavorited(this.offerInfo);
    }

    get isOfferReadyForBounceback(): boolean {
        return OfferItemView.isOfferReadyForBounceback(this.settings.calculateCurrentTime(), this.offerInfo);
    }

    get showRedeemNow(): boolean {
        return this._offerItemView && this._offerItemView.showRedeemNow;
    }

    get expired(): boolean {
        return this.daysRemaining === 0;
    }

    get unavailable(): boolean {
        return this._offerItemView && this._offerItemView.unavailable;
    }

    get canRedeemNow(): boolean {
        return this.showRedeemNow && !this.outsideOfHours;
    }

    get outsideOfHours(): boolean {
        return this.offerInfo && this.offerInfo.weeklyHours && !isWithinWeeklyHours(this.offerInfo.weeklyHours, this.displayTime);
    }

    get insideOfHours(): boolean {
        return this.offerInfo && this.offerInfo.weeklyHours && !this.outsideOfHours;
    }

    get daysRemaining(): number {
        if (this._offerItemView && this._offerItemView.showExpiration) {
            if (this._daysRemaining == null) {
                this._daysRemaining = OfferItemView.calculateDaysRemaining(this._currentDate, this.offerInfo);
            }
            return this._daysRemaining;
        } else {
            return null;
        }
    }

    get buttonLayout(): "promoteTheApp"|"giveAnOffer"|"notFound"|"noneClaimed"|"repeatUnlockable"|"mystery"|"obscured"|"obscuredRedeemNow"|"normal"|"redeemNow"|"onlineOnly"|"redeemed"|"readyForBounceback"|"expired"|"unavailable" {
        if (this.negativeOfferId) {
            if (this._offerInfo.id === -1) {
                return "promoteTheApp";
            } else if (this._offerInfo.id === -2) {
                return "giveAnOffer";
            } else if (this._offerInfo.id === -3) {
                return "notFound";
            } else if (this._offerInfo.id === -4) {
                return "noneClaimed";
            }
            return "promoteTheApp";
        } else if (this.obscured) {
            if (this.expired) {
                return "expired";
            } else if (this.unavailable) {
                return "unavailable";
            } else if (this.showRedeemNow) {
                return "obscuredRedeemNow";
            } else {
                return "obscured";
            }
        } else if (this.mystery) {
            return "mystery";
        } else if (!this.expired && !this.unavailable && this.isOfferRepeatUnlockable && this._offerItemView && this._offerItemView.showButtons) {
            return "repeatUnlockable";
        } else if (!this.expired && !this.unavailable && !this.isOfferRedeemed && !this.showRedeemNow && !this.isOfferOnlineOnly && this._offerItemView && this._offerItemView.showButtons) {
            return "normal";
        } else if (!this.expired && !this.unavailable && !this.isOfferRedeemed && this.showRedeemNow && this._offerItemView && this._offerItemView.showButtons) {
            if (this.outsideOfHours) {
                return "normal";
            } else {
                return "redeemNow";
            }
        } else if (!this.expired && !this.unavailable && !this.isOfferRedeemed && this.isOfferOnlineOnly && this._offerItemView && this._offerItemView.showButtons) {
            return "onlineOnly";
        } else if (this.isOfferRedeemed && !this.isOfferReadyForBounceback && this._offerItemView && this._offerItemView.showButtons) {
            if (this.outsideOfHours) {
                return "normal";
            } else {
                return "redeemed";
            }
        } else if (this.isOfferRedeemed && this.isOfferReadyForBounceback && this._offerItemView && this._offerItemView.showButtons) {
            return "readyForBounceback";
        } else if (this.unavailable && !this.isOfferRedeemed && this._offerItemView && this._offerItemView.showButtons) {
            return "unavailable";
        } else if (this.expired && !this.isOfferRedeemed && this._offerItemView && this._offerItemView.showButtons) {
            return "expired";
        }
        return "normal";
    }

    private _offerInfo: OfferInfo;
    private _offerItemView: OfferItemView = new OfferItemView();
    private _openOfferInfo: OfferInfo;
    private _currentDate: Date = null;
    private _daysRemaining: number = null;
    private _displayTime: moment.Moment = momentNow();

    constructor(
        private readonly notify: NotificationService,
        private readonly api: ApiService,
        private readonly cd: ChangeDetectorRef
    ) {
        // Use the component constructor to inject providers.
        this._currentDate = this.settings.calculateCurrentTime();
        // this._offerInfo = null;
    }

    ngOnInit(): void {
        // Init your component properties here.
        if (this.offerInfo) Log.debug(Log.Tag.OfferItem, "INIT ITEM: " + this.offerInfo.id);
    }

    ngAfterViewInit(): void {
        // do nothing
    }

    ngOnDestroy(): void {
        if (this.offerInfo) Log.debug(Log.Tag.OfferItem, "DESTROY ITEM: " + this.offerInfo.id);
    }

    onAction(action: OfferItemAction): void {
        this.action.emit(action);
    }

    // rename callback
    onIWantOfferTap(): void {
        this.action.emit({ type: OfferItemActionType.tapOpen });
    }

    onRevealTap(): void {
        this.action.emit({ type: OfferItemActionType.tapReveal });
    }

    onRepeatUnlockTap(): void {
        this.action.emit({ type: OfferItemActionType.tapRepeatUnlock });
    }

    onAcquireTap(): void {
        this.action.emit({ type: OfferItemActionType.tapFavorite });
    }

    onTapShare(): void {
        this.action.emit({ type: OfferItemActionType.tapShare });
    }

    onTapShareApp(): void {
        this.action.emit({ type: OfferItemActionType.tapShareApp });
    }

    onMerchantContact(): void {
        this.action.emit({ type: OfferItemActionType.tapMerchantContact });
    }

    onResetSearch(): void {
        this.action.emit({ type: OfferItemActionType.tapResetSearch });
    }
}
